import { useState, useMemo, useContext, useEffect } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { useMutation } from '@aesop-fables/scrinium';
import { RightDrawer } from '../../components';
import { PaidByEnum } from '../../models/LicenseData';
import { AvailablePricebooks, PricebookBundle } from '../../models/PricebookData';
import { AdminTierTypeEnum } from '../../services/drillDown';
import { ToastbarContext } from '../../App';
import { Toast } from '../../models/Toast';
import { useLoading } from '../../hooks/useLoading';
import { useAddOnPricebookLookup } from '../../hooks/useAddOnPricebookLookup';
import PricebookRestrictionSelector from './components/PricebookRestrictionSelector';
import {
  RestrictionMode,
  getRestrictionMode,
  getParentTierLabel,
  sortPricebooksByPayerAndName,
  sortPricebooksByInUseAndName,
  getFooterMessage,
} from './helpers/pricebookRestrictionUtils';
import {
  SaveTeamPricebookRestrictions,
  RevertTeamPricebookRestrictions,
  SaveOrgPricebookRestrictions,
  RevertOrgPricebookRestrictions,
} from '../../data/pricebook/mutations';

interface PricebookRestrictionsDrawerProps {
  drawerOpen: boolean;
  setDrawerOpen: React.Dispatch<React.SetStateAction<boolean>>;
  adminTier: AdminTierTypeEnum;
  availablePricebooks: AvailablePricebooks | undefined;
  parentAvailablePricebooks?: AvailablePricebooks | undefined;
  allPricebookBundles?: PricebookBundle[];
  inUsePricebookIds?: Set<number>;
  onValidateSave?: (
    mode: RestrictionMode,
    selectedIds: number[],
    visibleIds: Set<number>,
  ) => boolean;
  onSave?: (mode: RestrictionMode, pricebookIds: number[]) => void | Promise<void>;
  selectedMode?: RestrictionMode;
  selectedPricebookIds?: number[];
}

const PricebookRestrictionsDrawer: React.FC<PricebookRestrictionsDrawerProps> = ({
  drawerOpen,
  setDrawerOpen,
  adminTier,
  availablePricebooks,
  parentAvailablePricebooks,
  allPricebookBundles,
  inUsePricebookIds = new Set(),
  onValidateSave,
  onSave,
  selectedMode,
  selectedPricebookIds: selectedPricebookIdsProp,
}) => {
  const { setToast } = useContext(ToastbarContext);
  const { setLoading } = useLoading();
  const saveTeamRestrictions = useMutation(new SaveTeamPricebookRestrictions());
  const revertTeamRestrictions = useMutation(new RevertTeamPricebookRestrictions());
  const saveOrgRestrictions = useMutation(new SaveOrgPricebookRestrictions());
  const revertOrgRestrictions = useMutation(new RevertOrgPricebookRestrictions());

  const initialMode = getRestrictionMode(adminTier, availablePricebooks, parentAvailablePricebooks);
  const [restrictionMode, setRestrictionMode] = useState<RestrictionMode>(initialMode);
  const [selectedPricebookIds, setSelectedPricebookIds] = useState<Set<number>>(new Set());

  useEffect(() => {
    if (!drawerOpen) return;
    if (selectedMode) {
      setRestrictionMode(selectedMode);
      setSelectedPricebookIds(new Set(selectedPricebookIdsProp ?? []));
      return;
    }
    const currentMode = getRestrictionMode(
      adminTier,
      availablePricebooks,
      parentAvailablePricebooks,
    );
    setRestrictionMode(currentMode);
    if (currentMode === 'override' || currentMode === 'advanced') {
      setSelectedPricebookIds(
        new Set((availablePricebooks?.pricebookBundleDtos ?? []).map(pb => pb.pricebook.id)),
      );
    } else {
      setSelectedPricebookIds(new Set());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawerOpen]);

  const parentTierLabel = getParentTierLabel(adminTier, availablePricebooks);
  const tierLabel = adminTier.toLowerCase();
  const isInheritMode = restrictionMode === 'inherit';

  const { memberPaysPricebooks, orgPaysPricebooks, pricebooksWithAddOns } = useMemo(() => {
    const pricebooksForMode = (): PricebookBundle[] => {
      const parentOrFallback =
        parentAvailablePricebooks?.pricebookBundleDtos ??
        availablePricebooks?.pricebookBundleDtos ??
        [];
      switch (restrictionMode) {
        case 'inherit':
          return parentOrFallback;
        case 'override':
          return parentOrFallback;
        case 'advanced':
          return allPricebookBundles ?? [];
      }
    };
    const pricebooks = pricebooksForMode();
    // Premium add-on pricebooks are hidden; selecting the base pricebook pairs its add-on on the backend
    const sorted = sortPricebooksByPayerAndName(pricebooks.filter(pb => !pb.pricebook.addOn));
    const memberPays = sorted.filter(pb => pb.pricebook.paidBy === PaidByEnum.User);
    const orgPays = sorted.filter(pb => pb.pricebook.paidBy === PaidByEnum.Org);

    if (adminTier === AdminTierTypeEnum.Organization || adminTier === AdminTierTypeEnum.Team) {
      return {
        memberPaysPricebooks: sortPricebooksByInUseAndName(memberPays, inUsePricebookIds),
        orgPaysPricebooks: sortPricebooksByInUseAndName(orgPays, inUsePricebookIds),
        pricebooksWithAddOns: pricebooks,
      };
    }

    return {
      memberPaysPricebooks: memberPays,
      orgPaysPricebooks: orgPays,
      pricebooksWithAddOns: pricebooks,
    };
  }, [
    restrictionMode,
    parentAvailablePricebooks,
    availablePricebooks,
    allPricebookBundles,
    adminTier,
    inUsePricebookIds,
  ]);

  const addOnPricebookLookup = useAddOnPricebookLookup(pricebooksWithAddOns);

  const visiblePricebookIds = useMemo(() => {
    const ids = new Set<number>();
    for (const pb of memberPaysPricebooks) ids.add(pb.pricebook.id);
    for (const pb of orgPaysPricebooks) ids.add(pb.pricebook.id);
    return ids;
  }, [memberPaysPricebooks, orgPaysPricebooks]);

  const selectedVisibleIds = useMemo(
    () => new Set([...selectedPricebookIds].filter(id => visiblePricebookIds.has(id))),
    [selectedPricebookIds, visiblePricebookIds],
  );

  const { text: footerText, boldSegment } = getFooterMessage(
    restrictionMode,
    selectedVisibleIds.size,
    parentTierLabel,
    tierLabel,
  );
  const footerMessage = (() => {
    if (!boldSegment) return footerText;
    const [prefix, suffix] = footerText.split(boldSegment);
    return (
      <>
        {prefix}
        <strong>{boldSegment}</strong>
        {suffix}
      </>
    );
  })();

  const handleTogglePricebook = (pricebookId: number) => {
    if (isInheritMode) return;

    setSelectedPricebookIds(prev => {
      const next = new Set(prev);
      if (next.has(pricebookId)) {
        next.delete(pricebookId);
      } else {
        next.add(pricebookId);
      }
      return next;
    });
  };

  const handleSave = async () => {
    if (
      onValidateSave &&
      !onValidateSave(restrictionMode, [...selectedVisibleIds], visiblePricebookIds)
    ) {
      return;
    }

    if (
      adminTier === AdminTierTypeEnum.Organization &&
      (restrictionMode === 'override' || restrictionMode === 'advanced') &&
      selectedVisibleIds.size === 0
    ) {
      setToast(
        new Toast({
          severity: 'warning',
          open: true,
          message: `Choose at least one pricebook for this ${tierLabel}.`,
          autoHideDuration: 6000,
        }),
      );
      return;
    }

    if (onSave) {
      try {
        setLoading(true);
        await onSave(restrictionMode, [...selectedVisibleIds]);
        setToast(
          new Toast({
            severity: 'success',
            open: true,
            message: 'Pricebook restrictions updated',
            autoHideDuration: 6000,
          }),
        );
        setDrawerOpen(false);
      } catch (err) {
        console.error('Failed to save pricebook restrictions:', err);
        setToast(
          new Toast({
            severity: 'error',
            open: true,
            message: 'Failed to save pricebook restrictions.',
            autoHideDuration: 6000,
          }),
        );
      } finally {
        setLoading(false);
      }
      return;
    }

    try {
      setLoading(true);

      if (adminTier === AdminTierTypeEnum.Team) {
        if (restrictionMode === 'inherit') {
          await revertTeamRestrictions.action();
        } else {
          await saveTeamRestrictions.action([...selectedVisibleIds]);
        }
      } else if (adminTier === AdminTierTypeEnum.Organization) {
        if (restrictionMode === 'inherit') {
          await revertOrgRestrictions.action();
        } else {
          await saveOrgRestrictions.action([...selectedVisibleIds]);
        }
      }

      setToast(
        new Toast({
          severity: 'success',
          open: true,
          message: `${adminTier === AdminTierTypeEnum.Team ? 'Team' : 'Organization'} pricebook restrictions saved!`,
          autoHideDuration: 6000,
        }),
      );
      setDrawerOpen(false);
    } catch (err) {
      console.error('Failed to save pricebook restrictions:', err);
      setToast(
        new Toast({
          severity: 'error',
          open: true,
          message: 'Failed to save pricebook restrictions.',
          autoHideDuration: 6000,
        }),
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <RightDrawer
      open={drawerOpen}
      setOpen={setDrawerOpen}
      smallTitle={`Edit ${tierLabel} settings`}
      title='Restrict pricebook options'
      footerShadow
      footer={
        <Box display='flex' justifyContent='space-between' alignItems='center'>
          <Button
            variant='outlined'
            color='secondary'
            onClick={() => setDrawerOpen(false)}
            sx={{ minWidth: '100px', typography: 'p14SemiBold' }}>
            Cancel
          </Button>
          <Typography
            variant='p16'
            color='secondary.main'
            sx={{ textAlign: 'center', flex: 1, mx: 2 }}>
            {footerMessage}
          </Typography>
          <Button
            variant='contained'
            color='primary'
            onClick={handleSave}
            sx={{ minWidth: '100px', typography: 'p14SemiBold' }}>
            Save
          </Button>
        </Box>
      }>
      <PricebookRestrictionSelector
        heading={
          parentTierLabel
            ? `Would you like to inherit ${parentTierLabel} restrictions or override?`
            : 'Would you like to restrict pricebook options?'
        }
        parentTierLabel={parentTierLabel}
        tierLabel={tierLabel}
        mode={restrictionMode}
        onModeChange={setRestrictionMode}
        memberPaysPricebooks={memberPaysPricebooks}
        orgPaysPricebooks={orgPaysPricebooks}
        addOnPricebookLookup={addOnPricebookLookup}
        inUsePricebookIds={inUsePricebookIds}
        selectedPricebookIds={selectedPricebookIds}
        onTogglePricebook={handleTogglePricebook}
      />
    </RightDrawer>
  );
};

export default PricebookRestrictionsDrawer;
